import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHero, Section, Eyebrow } from "@/components/PageParts";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { IllustrativeBanner } from "@/components/Disclaimers";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { Calculator, AlertTriangle } from "lucide-react";

const money = (n) => "$" + (Math.round(n || 0)).toLocaleString();

export default function CommissionOpportunities() {
  const nav = useNavigate();
  const [opps, setOpps] = useState([]);
  useEffect(() => { api.get("/commission-opportunities").then((r) => setOpps(r.data)).catch(() => {}); }, []);
  const illustrative = opps.filter((o) => o.kind === "illustrative");
  const active = opps.filter((o) => o.kind === "active" && o.active);

  return (
    <div>
      <PageHero eyebrow="Commission Opportunity Center" title="How placement economics can work"
        subtitle="We separate two concepts: illustrative commission economics that teach how the business works, and actual approved provider opportunities that require admin-approved written agreements." />
      <Section className="py-16">
        <Tabs defaultValue="illustrative">
          <TabsList data-testid="opp-tabs">
            <TabsTrigger value="illustrative" data-testid="tab-illustrative">Illustrative economics</TabsTrigger>
            <TabsTrigger value="active" data-testid="tab-active">Active opportunities ({active.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="illustrative" className="mt-6">
            <IllustrativeBanner className="mb-6" />
            <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
              {illustrative.map((o) => (
                <Card key={o.id} className="p-6" data-testid={`opp-illustrative-${o.id}`}>
                  <Badge className="bg-amberbrand/10 text-amberbrand hover:bg-amberbrand/10">ILLUSTRATIVE — NOT GUARANTEED</Badge>
                  <h3 className="mt-4 font-heading text-lg font-semibold text-navy">{o.title}</h3>
                  <p className="mt-1 text-xs uppercase tracking-wide text-slate-400">{o.service_category}</p>
                  <p className="mt-3 text-sm text-slate-500">{o.body}</p>
                  <div className="mt-4 rounded-lg bg-emeraldbrand/10 p-4">
                    <p className="text-xs uppercase tracking-wide text-emeraldbrand">Illustrative potential commission</p>
                    <p className="font-heading text-2xl font-bold text-emeraldbrand">{money(o.illustrative_commission)}</p>
                  </div>
                  {o.compliance_status && o.compliance_status !== "APPROVED" && (
                    <p className="mt-3 flex items-center gap-1.5 text-xs font-semibold text-amberbrand">
                      <AlertTriangle className="h-3.5 w-3.5" /> {o.compliance_status}
                    </p>
                  )}
                </Card>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="active" className="mt-6">
            {active.length === 0 ? (
              <Card className="p-10 text-center">
                <AlertTriangle className="mx-auto h-8 w-8 text-amberbrand" />
                <h3 className="mt-4 font-heading text-xl font-semibold text-navy">No active opportunities published</h3>
                <p className="mx-auto mt-2 max-w-lg text-slate-500">
                  Actual provider opportunities require admin-approved written provider agreements and compliance review
                  before they are published. Illustrative examples are for education only.
                </p>
              </Card>
            ) : (
              <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
                {active.map((o) => (
                  <Card key={o.id} className="p-6" data-testid={`opp-active-${o.id}`}>
                    <Badge className="bg-emeraldbrand/10 text-emeraldbrand hover:bg-emeraldbrand/10">ACTIVE</Badge>
                    <h3 className="mt-4 font-heading text-lg font-semibold text-navy">{o.title}</h3>
                    <p className="mt-1 text-xs uppercase tracking-wide text-slate-400">{o.service_category} · {o.location}</p>
                    <p className="mt-3 text-sm text-slate-500">{o.requirements}</p>
                    <div className="mt-4 rounded-lg bg-emeraldbrand/10 p-4">
                      <p className="text-xs uppercase tracking-wide text-emeraldbrand">Approved commission</p>
                      <p className="font-heading text-2xl font-bold text-emeraldbrand">{money(o.illustrative_commission)}</p>
                    </div>
                    {o.terms && <p className="mt-3 text-xs text-slate-400">{o.terms}</p>}
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>

        <Card className="mt-12 flex flex-col items-center gap-4 bg-navy p-10 text-center text-white">
          <Calculator className="h-9 w-9 text-clinical" />
          <h3 className="font-heading text-2xl font-semibold">Model your own scenarios</h3>
          <Button data-testid="opp-calculator-btn" onClick={() => nav("/commission-calculator")} className="bg-clinical hover:bg-clinical/90">
            Open commission calculator
          </Button>
        </Card>
      </Section>
    </div>
  );
}
